/**
 * Public combo detail page. Route: /catalogo/combo/:id
 *
 * Mirrors the StorefrontProduct layout so combos feel like a first-class
 * product from the customer's point of view: hero photo, name + description,
 * price + savings, quantity stepper, add-to-cart. Adds a "Contenido del combo"
 * section listing the member products (name, brand, qty, thumbnail) so the
 * buyer sees exactly what they receive.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Minus, Plus, ShoppingCart, Truck, Package, MagnifyingGlassPlus, WhatsappLogo } from "@phosphor-icons/react";
import PhotoLightbox from "@/components/PhotoLightbox";
import { useCart } from "@/context/CartContext";
import { CartFloatingButton, CartDrawer } from "@/components/storefront/CartDrawer";

const API = process.env.REACT_APP_BACKEND_URL;
const fmtPrice = (n, cur) =>
  `${cur === "USD" ? "US$" : "$"}${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function StorefrontCombo() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [combo, setCombo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [qty, setQty] = useState(1);
  const [activePhoto, setActivePhoto] = useState(0);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${API}/api/public/combos/${id}`);
        if (!cancelled) { setCombo(data); setError(false); }
      } catch { if (!cancelled) setError(true); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const photos = combo?.photos || [];
  const maxQty = combo?.max_qty || 0;
  const disabled = !combo || maxQty <= 0;
  const discountPct = combo?.original_total && combo.price && combo.original_total > combo.price
    ? Math.round((1 - combo.price / combo.original_total) * 100) : 0;

  const total = useMemo(() => Number(combo?.price || 0) * qty, [combo, qty]);

  const addToCart = () => {
    if (!combo || disabled) return;
    addItem({
      id: combo.id,
      name: combo.name,
      brand: "COMBO",
      photos: photos.slice(0, 1),
      store_price: combo.price,
      sale_currency: combo.currency || "MXN",
      max_qty: maxQty,
      kind: "combo",
    }, qty);
  };

  if (loading) {
    return <div className="min-h-screen bg-[#faf8f4] flex items-center justify-center text-zinc-500">Cargando combo…</div>;
  }
  if (error || !combo) {
    return (
      <div className="min-h-screen bg-[#faf8f4] flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-white border border-zinc-200 rounded-lg p-8 text-center">
          <Package size={44} weight="duotone" className="mx-auto text-zinc-400 mb-3" />
          <h1 className="font-heading text-2xl font-extrabold text-zinc-900 mb-1">Combo no disponible</h1>
          <p className="text-sm text-zinc-500 mb-4">Es posible que se haya agotado o retirado del catálogo.</p>
          <Link to="/catalogo" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#002FA7] hover:underline"><ArrowLeft size={14} weight="bold" /> Volver al catálogo</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf8f4]" data-testid="storefront-combo-page">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/catalogo" className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-700 hover:text-[#002FA7]">
            <ArrowLeft size={16} weight="bold" /> Volver
          </Link>
          <CartFloatingButton inline />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Photo column */}
        <div>
          <div className="relative bg-white border border-zinc-200 rounded-lg overflow-hidden aspect-square">
            <span className="absolute top-3 left-3 z-10 bg-[#7E57C2] text-white text-[10px] font-extrabold px-2 py-0.5 rounded-sm tracking-widest shadow">
              {combo.badge_label || "COMBO"}
            </span>
            {discountPct > 0 && (
              <span className="absolute top-11 left-3 z-10 bg-white border border-[#7E57C2] text-[#7E57C2] text-[10px] font-bold px-2 py-0.5 rounded-sm shadow-sm">
                -{discountPct}%
              </span>
            )}
            {combo.free_shipping && (
              <span className="absolute top-3 right-3 z-10 bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-sm inline-flex items-center gap-1">
                <Truck size={11} weight="bold" /> Envío gratis
              </span>
            )}
            {photos[activePhoto] ? (
              <button
                type="button"
                onClick={() => setLightbox({ open: true, index: activePhoto })}
                className="absolute inset-0 cursor-zoom-in group"
                data-testid="combo-hero-photo"
              >
                <img src={photos[activePhoto]} alt={combo.name} className="w-full h-full object-contain" />
                <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm p-2 rounded-full opacity-0 group-hover:opacity-100 transition">
                  <MagnifyingGlassPlus size={14} weight="bold" />
                </div>
              </button>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-zinc-300"><Package size={48} weight="duotone" /></div>
            )}
          </div>
          {photos.length > 1 && (
            <div className="mt-3 grid grid-cols-6 gap-2">
              {photos.map((p, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActivePhoto(i)}
                  className={`aspect-square rounded overflow-hidden border-2 ${i === activePhoto ? "border-[#7E57C2]" : "border-transparent"}`}
                  data-testid={`combo-thumb-${i}`}
                >
                  <img src={p} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info column */}
        <div className="space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#7E57C2] font-mono font-semibold">
              {combo.members?.length || 0} producto{combo.members?.length === 1 ? "" : "s"} incluidos
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-zinc-900 mt-1">{combo.name}</h1>
          </div>

          {combo.description && (
            <p className="text-sm text-zinc-600 whitespace-pre-line leading-relaxed">{combo.description}</p>
          )}

          {/* Price block */}
          <div className="bg-white border border-zinc-200 rounded-lg p-4">
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="font-heading text-4xl font-extrabold text-[#7E57C2]">
                {fmtPrice(combo.price, combo.currency)}
              </span>
              {combo.original_total && combo.original_total > combo.price && (
                <span className="text-lg text-zinc-400 line-through numeric">
                  {fmtPrice(combo.original_total, combo.currency)}
                </span>
              )}
              <span className="text-xs text-zinc-500 font-mono">{combo.currency || "MXN"}</span>
            </div>
            {combo.savings > 0 && (
              <div className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2 py-1">
                Ahorras {fmtPrice(combo.savings, combo.currency)}
              </div>
            )}
          </div>

          {/* Members list */}
          {combo.members && combo.members.length > 0 && (
            <section className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="combo-members-list">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono font-semibold mb-3">
                Contenido del combo
              </div>
              <ul className="space-y-2">
                {combo.members.map((m, i) => (
                  <li key={i} className="flex items-center gap-3 py-2 border-b border-zinc-100 last:border-none">
                    <div className="w-12 h-12 rounded overflow-hidden bg-zinc-100 shrink-0 border border-zinc-200">
                      {m.photo ? (
                        <img src={m.photo} alt={m.name} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-zinc-300"><Package size={18} weight="duotone" /></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-zinc-900 truncate">{m.name}</div>
                      {m.brand && <div className="text-[11px] text-zinc-500">{m.brand}</div>}
                    </div>
                    <div className="text-sm font-bold text-[#7E57C2] font-mono">×{m.qty}</div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Add to cart */}
          <div className="bg-white border border-zinc-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Cantidad</span>
              <div className="inline-flex items-center border border-zinc-300 rounded-full overflow-hidden">
                <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} disabled={qty <= 1} className="px-3 py-1 disabled:opacity-40" data-testid="combo-qty-minus"><Minus size={12} /></button>
                <span className="px-3 numeric font-bold text-sm w-8 text-center" data-testid="combo-qty-value">{qty}</span>
                <button type="button" onClick={() => setQty(Math.min(maxQty, qty + 1))} disabled={qty >= maxQty} className="px-3 py-1 disabled:opacity-40" data-testid="combo-qty-plus"><Plus size={12} /></button>
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-zinc-100 pt-3">
              <span className="text-xs text-zinc-500 font-mono uppercase tracking-widest">Total</span>
              <span className="font-heading text-2xl font-extrabold text-[#7E57C2] numeric">{fmtPrice(total, combo.currency)}</span>
            </div>
            <button
              type="button"
              onClick={addToCart}
              disabled={disabled}
              className="w-full h-12 rounded-full bg-[#7E57C2] hover:bg-[#5E37A2] text-white font-semibold inline-flex items-center justify-center gap-2 disabled:bg-zinc-300 disabled:cursor-not-allowed transition-colors"
              data-testid="combo-add-to-cart"
            >
              <ShoppingCart size={16} weight="bold" />
              {disabled ? "Agotado" : `Agregar ${qty > 1 ? `${qty} combos` : "combo"} al carrito`}
            </button>
            {combo.free_shipping && (
              <div className="text-[11px] text-emerald-700 inline-flex items-center gap-1 justify-center w-full">
                <Truck size={12} weight="bold" /> Envío gratis incluido
              </div>
            )}
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`Hola! Me interesa el combo "${combo.name}" (${fmtPrice(combo.price, combo.currency)}). ¿Está disponible? ${window.location.href}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full h-11 rounded-full border border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10 font-semibold inline-flex items-center justify-center gap-2 text-sm"
              data-testid="combo-whatsapp-btn"
            >
              <WhatsappLogo size={14} weight="bold" /> Consultar por WhatsApp
            </a>
          </div>
        </div>
      </div>

      <PhotoLightbox
        open={lightbox.open}
        photos={photos}
        initialIndex={lightbox.index}
        title={combo.name}
        onClose={() => setLightbox({ open: false, index: 0 })}
      />
      <CartDrawer />
    </div>
  );
}
